import Head from 'next/head';
import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { guideImage, imageUrl } from '@/components/interior-landing/assets';
import { INTERIOR_STYLE_COUNT } from '@/components/interior-landing/content';
import FinalCta from '@/components/interior-landing/FinalCta';
import InteriorFaq from '@/components/interior-landing/InteriorFaq';
import InteriorHero from '@/components/interior-landing/InteriorHero';
import { HUB_PATH } from '@/components/interior-landing/pages';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';

import GuideCards from './GuideCards';
import { guidePath, STYLES_PATH } from './pages';
import type { StylesHubProps } from './server';

const SITE = 'https://easy-peasy.ai';

/**
 * The style guides' index (/interior-design-styles): every style on the same
 * living room, bedroom and kitchen, a link to each guide, how to find your
 * style and questions.
 */
export default function StylesHubPage({ page, guides, steps }: StylesHubProps) {
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { name: 'Easy-Peasy.AI', item: SITE },
        { name: 'Interior Design Styles', item: `${SITE}${STYLES_PATH}` },
      ].map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        ...crumb,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Interior design styles',
      itemListElement: guides.map((guide, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `${guide.name} interior design`,
        url: `${SITE}${guidePath(guide.slug)}`,
      })),
    },
  ];

  return (
    <main className={clsxm('relative overflow-x-clip', tone.page)}>
      <Seo
        templateTitle={page.seoTitle}
        description={page.seoDescription}
        image={imageUrl(guideImage('japandi', 'living-room'), 1200)}
        structuredData={false}
      />
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>

      <InteriorHero page={page} />

      <section id='guides' className='py-20 sm:py-28'>
        <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
          <SectionHeading
            title='Ten styles, explained.'
            tail='Pick one to read its guide.'
          />
          <GuideCards
            guides={guides}
            sizes='(min-width: 1024px) 18vw, 46vw'
            className='mt-14 grid-cols-2 lg:grid-cols-5'
          />
        </div>
      </section>

      <section className='bg-white py-20 dark:bg-[#0f0f18] sm:py-28'>
        <div className='mx-auto grid max-w-7xl gap-x-16 gap-y-12 px-5 sm:px-6 lg:grid-cols-12 lg:px-8'>
          <div className='lg:col-span-4'>
            <SectionHeading size='md' title='How to find your style.' />
            <p className={clsxm('mt-5 text-[17px] leading-7', tone.body)}>
              The AI Interior Designer has {INTERIOR_STYLE_COUNT} styles.{' '}
              <Link
                href={`${HUB_PATH}#styles`}
                className='font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400'
              >
                See them all
              </Link>
              .
            </p>
          </div>
          <ol className='grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:col-span-8'>
            {steps.map((step, index) => (
              <li
                key={step.title}
                className='border-t border-[#0f0f28]/10 pt-6 dark:border-white/10'
              >
                <span className='text-sm font-semibold tabular-nums text-primary-600 dark:text-primary-400'>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3
                  className={clsxm(
                    'mt-3 text-xl font-semibold leading-snug',
                    tone.ink,
                  )}
                >
                  {step.title}
                </h3>
                <p className={clsxm('mt-2 text-base leading-7', tone.body)}>
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <InteriorFaq page={page} sharedFaqs={false} />

      <section className='pb-6'>
        <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
          <Link
            href={HUB_PATH}
            className='inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400'
          >
            More about the AI Interior Designer
            <ArrowGlyph />
          </Link>
        </div>
      </section>

      <FinalCta page={page} />
      <Footer />
    </main>
  );
}
